import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { AmountInput } from "../components/ui/amount-input";
import { toast } from "sonner";
import { formatINR, formatDateTime, fileToBase64, waLink } from "../lib/format";
import { openAuthDownload } from "../lib/download";
import { ArrowDownRight, Banknote, MessageCircle, Phone, Receipt, FileText, FileDown, CalendarClock, Users, Pencil, Archive as ArchiveIcon, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import GroupMembersPanel from "../components/GroupMembersPanel";
import ScheduleTable from "../components/ScheduleTable";
import { PaymentActions } from "../components/PaymentActions";
import { EntryDetailDialog } from "../components/EntryDetailDialog";
import { BackdateFields, backdatePayload } from "../components/BackdateFields";
import { useDuplicateGuard, todayIST } from "../components/DuplicateGuard";

export default function LoanDetail() {
    const { id } = useParams();
    const { t } = useLang();
    const { user } = useAuth();
    const { guard, dialog: dupDialog } = useDuplicateGuard();
    const isAdmin = user?.role === "admin";
    const [loan, setLoan] = useState(null);
    const [payments, setPayments] = useState([]);
    const [detailId, setDetailId] = useState(null);
    const [members, setMembers] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ amount: "", mode: "cash", notes: "", proof: "", member_id: "" });
    const [backdate, setBackdate] = useState({});
    const [editPay, setEditPay] = useState(null);
    const [payForm, setPayForm] = useState({ interest_paid: 0, principal_paid: 0, mode: "cash", notes: "" });

    async function savePaymentEdit() {
        try {
            await api.put(`/payments/${editPay.id}`, payForm);
            toast.success("Payment updated");
            setEditPay(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    async function deletePayment(p) {
        if (!window.confirm("Delete this payment? Loan totals will recompute.")) return;
        try {
            await api.delete(`/payments/${p.id}`);
            toast.success("Payment deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function load() {
        const [a, b] = await Promise.all([api.get(`/loans/${id}`), api.get(`/loans/${id}/payments`)]);
        setLoan(a.data);
        setPayments(b.data);
        if (a.data.loan_type === "group") {
            api.get(`/loans/${id}/members`).then((r) => setMembers(r.data.members || []));
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    async function submitCollection(e) {
        e.preventDefault();
        if (!form.amount || form.amount <= 0) return toast.error("Enter amount");
        await guard({
            collection_type: "loan", loan_id: id, customer_id: loan.customer.id, amount: Number(form.amount),
            on_date: backdate.enabled && backdate.collection_datetime ? backdate.collection_datetime.slice(0, 10) : todayIST(),
        }, doSubmitCollection);
    }

    async function doSubmitCollection() {
        setBusy(true);
        try {
            await api.post("/collections", {
                loan_id: id,
                customer_id: loan.customer.id,
                collection_type: "loan",
                amount: Number(form.amount),
                mode: form.mode,
                notes: form.notes,
                proof: form.proof,
                member_id: form.member_id || null,
                ...backdatePayload(backdate),
            });
            setBackdate({});
            toast.success("Submitted for admin approval");
            setOpen(false);
            setForm({ amount: "", mode: "cash", notes: "", proof: "", member_id: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    if (!loan) return <div className="text-slate-500">Loading…</div>;
    const c = loan.customer || {};

    return (
        <div className="space-y-5">
            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <Link
                            to={`/customers/${c.id}`}
                            className="text-xs text-blue-900 font-semibold uppercase tracking-wider"
                            data-testid="back-to-customer"
                        >
                            ← {c.name}
                        </Link>
                        <h1 className="font-display text-2xl font-bold text-slate-900 mt-1 capitalize">
                            {loan.loan_type} {t("loan")}
                        </h1>
                        <div className="text-xs text-slate-500">{loan.loan_code}</div>
                    </div>
                    <Badge
                        className={
                            loan.status === "active"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                : "bg-slate-100 text-slate-700"
                        }
                    >
                        {loan.status}
                    </Badge>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100">
                    <Info label={t("outstanding")} value={formatINR(loan.outstanding_principal)} accent />
                    <Info label={t("principal")} value={formatINR(loan.principal)} />
                    <Info label={t("interest_rate")} value={`${loan.interest_rate}% / mo`} />
                    <InterestBreakdown breakdown={loan.interest_breakdown} fallback={loan.next_interest_estimate} />
                    <Info label={t("interest_paid")} value={formatINR(loan.total_interest_paid)} />
                    <Info label={t("principal_paid")} value={formatINR(loan.total_principal_paid)} />
                    <Info label={t("tenure_months")} value={loan.tenure_months} />
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                data-testid="collect-emi-btn"
                                className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap"
                                disabled={loan.status !== "active"}
                            >
                                <Banknote className="h-4 w-4 mr-1.5" />
                                {t("new_collection")}
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom" className="rounded-t-2xl">
                            <SheetHeader>
                                <SheetTitle>{t("new_collection")}</SheetTitle>
                            </SheetHeader>
                            <form onSubmit={submitCollection} className="space-y-3 mt-3">
                                <div>
                                    <Label>{t("amount")}</Label>
                                    <AmountInput
                                        data-testid="collection-amount"
                                        required
                                        value={form.amount}
                                        onChange={(v) => setForm((s) => ({ ...s, amount: v }))}
                                        className="h-11 rounded-xl"
                                    />
                                </div>
                                <div>
                                    <Label>{t("mode")}</Label>
                                    <Select
                                        value={form.mode}
                                        onValueChange={(v) => setForm((s) => ({ ...s, mode: v }))}
                                    >
                                        <SelectTrigger className="h-11 rounded-xl" data-testid="collection-mode">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="cash">Cash</SelectItem>
                                            <SelectItem value="upi">UPI</SelectItem>
                                            <SelectItem value="qr">Bank QR</SelectItem>
                                            <SelectItem value="bank">Bank Transfer</SelectItem>
                                            <SelectItem value="other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>{t("notes")}</Label>
                                    <Textarea
                                        rows={2}
                                        value={form.notes}
                                        onChange={(e) =>
                                            setForm((s) => ({ ...s, notes: e.target.value }))
                                        }
                                        className="rounded-xl"
                                        data-testid="collection-notes"
                                    />
                                </div>
                                {loan.loan_type === "group" && members.length > 0 && (
                                    <div>
                                        <Label>Member (optional)</Label>
                                        <Select
                                            value={form.member_id || "none"}
                                            onValueChange={(v) => setForm((s) => ({ ...s, member_id: v === "none" ? "" : v }))}
                                        >
                                            <SelectTrigger className="h-11 rounded-xl" data-testid="collection-member">
                                                <SelectValue placeholder="Whole group" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Whole group (no split)</SelectItem>
                                                {members.map((m) => (
                                                    <SelectItem key={m.id} value={m.id}>
                                                        {m.name} · share {formatINR(m.share_amount)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div>
                                    <Label>{t("proof")}</Label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        data-testid="collection-proof"
                                        onChange={async (e) => {
                                            const f = e.target.files?.[0];
                                            if (!f) return;
                                            const b = await fileToBase64(f);
                                            setForm((s) => ({ ...s, proof: b }));
                                        }}
                                        className="block text-sm"
                                    />
                                    {form.proof && (
                                        <img
                                            src={form.proof}
                                            alt="proof"
                                            className="mt-2 h-20 rounded-lg border"
                                        />
                                    )}
                                </div>
                                <BackdateFields value={backdate} onChange={setBackdate} />
                                <Button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="submit-collection-btn"
                                    className="w-full h-12 rounded-xl bg-blue-900 hover:bg-blue-800"
                                >
                                    {busy ? "Submitting…" : t("submit_collection")}
                                </Button>
                                <p className="text-xs text-amber-700 text-center">
                                    ⚠ {t("pending_admin_approval")}
                                </p>
                            </form>
                        </SheetContent>
                    </Sheet>

                    {c.mobile && (
                        <>
                            <a
                                href={`tel:${c.mobile}`}
                                className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold tap"
                                data-testid="call-cust-from-loan"
                            >
                                <Phone className="h-4 w-4" /> {t("call")}
                            </a>
                            <a
                                href={waLink(
                                    c.mobile,
                                    `Hi ${c.name}, your loan ${loan.loan_code} outstanding is ${formatINR(loan.outstanding_principal)}.`
                                )}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="wa-cust-from-loan"
                                className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold tap"
                            >
                                <MessageCircle className="h-4 w-4" /> WhatsApp
                            </a>
                        </>
                    )}

                    {isAdmin && (
                        <Link to={`/loans/${loan.id}/edit`}>
                            <Button
                                variant="outline"
                                className="h-11 rounded-xl tap font-semibold"
                                data-testid="edit-loan-btn"
                            >
                                <Pencil className="h-4 w-4 mr-1.5" /> Edit Loan
                            </Button>
                        </Link>
                    )}
                    {isAdmin && !loan.archived && (
                        <Button
                            variant="outline"
                            className="h-11 rounded-xl text-amber-700 border-amber-200 hover:bg-amber-50 tap"
                            data-testid="archive-loan-btn"
                            onClick={async () => {
                                const reason = prompt("Reason for archiving (optional):") ?? "";
                                try {
                                    await api.post(`/loans/${loan.id}/archive`, { reason });
                                    toast.success("Loan archived");
                                    window.location.href = `/customers/${c.id}`;
                                } catch (e) {
                                    toast.error(formatApiError(e));
                                }
                            }}
                        >
                            <ArchiveIcon className="h-4 w-4 mr-1.5" /> Archive
                        </Button>
                    )}
                    {isAdmin && (
                        <Button
                            variant="outline"
                            className="h-11 rounded-xl"
                            data-testid="loan-sanction-cert"
                            onClick={() => openAuthDownload(`/certificates/loan-sanction/${loan.id}`)}
                        >
                            <FileText className="h-4 w-4 mr-1.5" /> Sanction Certificate
                        </Button>
                    )}
                    {isAdmin && loan.status === "closed" && (
                        <Button
                            variant="outline"
                            className="h-11 rounded-xl"
                            data-testid="loan-closure-cert"
                            onClick={() => openAuthDownload(`/certificates/loan-closure/${loan.id}`)}
                        >
                            <FileText className="h-4 w-4 mr-1.5" /> Closure Certificate
                        </Button>
                    )}
                    {isAdmin && (
                        <Button
                            variant="outline"
                            className="h-11 rounded-xl"
                            data-testid="loan-passbook"
                            onClick={() => openAuthDownload(`/passbooks/loan/${loan.id}`)}
                        >
                            <FileDown className="h-4 w-4 mr-1.5" /> Download Passbook
                        </Button>
                    )}
                </div>
            </Card>

            <GroupMembersPanel
                loanId={loan.id}
                loanType={loan.loan_type}
                principal={loan.principal}
            />

            <Tabs defaultValue="passbook">
                <TabsList className="bg-white border border-slate-100 rounded-xl">
                    <TabsTrigger value="passbook" data-testid="tab-passbook">
                        <Receipt className="h-4 w-4 mr-1.5" /> {t("passbook")}
                    </TabsTrigger>
                    <TabsTrigger value="schedule" data-testid="tab-schedule">
                        <CalendarClock className="h-4 w-4 mr-1.5" /> Schedule
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="passbook" className="mt-4">
                    {/* Passbook timeline */}
                    <Card className="rounded-2xl border-slate-100">
                        <div className="p-5 pb-2 flex items-center gap-2">
                            <Receipt className="h-4 w-4 text-blue-900" />
                            <h2 className="font-display text-lg font-bold text-slate-900">
                                {t("passbook")}
                            </h2>
                        </div>
                {payments.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400">{t("no_data")}</div>
                ) : (
                    <ol className="relative ml-6 my-5 mr-5 border-l border-slate-200 space-y-5">
                        {payments
                            .slice()
                            .reverse()
                            .map((p) => (
                                <li
                                    key={p.id}
                                    className="ml-5 relative"
                                    data-testid={`passbook-entry-${p.id}`}
                                >
                                    <div className="timeline-dot absolute -left-[27px] top-1.5" />
                                    <div className="flex items-center justify-between flex-wrap gap-2 cursor-pointer rounded-lg hover:bg-slate-50 -mx-1 px-1"
                                        role="button" tabIndex={0}
                                        data-testid={`passbook-entry-open-${p.id}`}
                                        onClick={() => setDetailId(p.id)}
                                        onKeyDown={(e) => { if (e.key === "Enter") setDetailId(p.id); }}>
                                        <div className="text-xs text-slate-500">
                                            {formatDateTime(p.collection_datetime || p.payment_date)} ·{" "}
                                            <span className="uppercase">{p.mode}</span> ·{" "}
                                            {p.staff}
                                        </div>
                                        <div className="num font-bold text-slate-900">
                                            {formatINR(p.total)}
                                        </div>
                                    </div>
                                    <div className="mt-2 grid sm:grid-cols-3 gap-2 text-xs">
                                        <div className="rounded-lg bg-amber-50 text-amber-900 px-2.5 py-1.5">
                                            {t("interest_paid")}: <b>{formatINR(p.interest_paid)}</b>
                                        </div>
                                        <div className="rounded-lg bg-emerald-50 text-emerald-900 px-2.5 py-1.5 flex items-center gap-1">
                                            <ArrowDownRight className="h-3.5 w-3.5" />
                                            {formatINR(p.principal_paid)}{" "}
                                            {t("deducted_from_principal")}
                                        </div>
                                        <div className="rounded-lg bg-slate-100 text-slate-700 px-2.5 py-1.5">
                                            {t("balance_after")}:{" "}
                                            <b>{formatINR(p.balance_after)}</b>
                                        </div>
                                    </div>
                                    {p.notes && (
                                        <div className="mt-1.5 text-xs text-slate-500 italic">
                                            {p.notes}
                                        </div>
                                    )}
                                    {isAdmin && (
                                        <PaymentActions payment={p} kind="loan" onDone={load} />
                                    )}
                                </li>
                            ))}
                    </ol>
                )}
            </Card>
                </TabsContent>

                <TabsContent value="schedule" className="mt-4">
                    <ScheduleTable loanId={loan.id} />
                </TabsContent>
            </Tabs>

            <Dialog open={!!editPay} onOpenChange={(o) => { if (!o) setEditPay(null); }}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Payment</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Interest Paid (₹)</Label>
                            <AmountInput
                                data-testid="edit-pay-interest"
                                value={payForm.interest_paid}
                                onChange={(v) => setPayForm((s) => ({ ...s, interest_paid: v }))}
                                className="h-11 rounded-xl"
                            />
                        </div>
                        <div>
                            <Label>Principal Paid (₹)</Label>
                            <AmountInput
                                data-testid="edit-pay-principal"
                                value={payForm.principal_paid}
                                onChange={(v) => setPayForm((s) => ({ ...s, principal_paid: v }))}
                                className="h-11 rounded-xl"
                            />
                        </div>
                        <div>
                            <Label>Mode</Label>
                            <Select value={payForm.mode} onValueChange={(v) => setPayForm((s) => ({ ...s, mode: v }))}>
                                <SelectTrigger className="h-11 rounded-xl" data-testid="edit-pay-mode">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cash">Cash</SelectItem>
                                    <SelectItem value="upi">UPI</SelectItem>
                                    <SelectItem value="qr">Bank QR</SelectItem>
                                    <SelectItem value="bank">Bank Transfer</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Textarea
                                rows={2}
                                value={payForm.notes}
                                onChange={(e) => setPayForm((s) => ({ ...s, notes: e.target.value }))}
                                className="rounded-xl"
                                data-testid="edit-pay-notes"
                            />
                        </div>
                        <div className="text-xs text-slate-500">
                            Loan totals and outstanding balance will be recomputed automatically.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditPay(null)}>Cancel</Button>
                        <Button
                            onClick={savePaymentEdit}
                            data-testid="save-edit-payment"
                            className="bg-blue-900 hover:bg-blue-800"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <EntryDetailDialog paymentId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />
            {dupDialog}
        </div>
    );
}

function Info({ label, value, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                {label}
            </div>
            <div
                className={`num text-base font-bold mt-0.5 ${accent ? "text-blue-900 text-lg" : "text-slate-900"}`}
            >
                {value}
            </div>
        </div>
    );
}

function InterestBreakdown({ breakdown, fallback }) {
    const total = breakdown?.total_pending ?? 0;
    const nextDue = breakdown?.next_due_date;
    const nextDueLabel = nextDue
        ? new Date(nextDue + "T00:00:00").toLocaleDateString("en-IN", {
              day: "2-digit", month: "short", year: "numeric",
          })
        : null;
    return (
        <div className="sm:col-span-2" data-testid="interest-breakdown">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                Total Interest Pending
            </div>
            <div
                className="num text-lg font-bold text-rose-700 mt-0.5"
                data-testid="interest-total-pending"
            >
                {formatINR(total)}
            </div>
            <div className="mt-1.5 pt-1.5 border-t border-slate-100">
                <div className="text-[10px] text-slate-500 font-medium">
                    Next Interest Due
                </div>
                <div className="num text-sm font-semibold text-slate-700">
                    {nextDueLabel || "—"}
                    {breakdown?.next_due_amount != null ? (
                        <span className="ml-1 text-xs font-normal text-slate-500" data-testid="interest-next-due-amount">
                            · {formatINR(breakdown.next_due_amount)}
                        </span>
                    ) : fallback ? (
                        <span className="ml-1 text-xs font-normal text-slate-500">
                            · {formatINR(fallback)}/mo
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
